import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import { useDropzone } from "react-dropzone"
import { FormattedMessage, useIntl } from "react-intl"
import { useNavigate, useSearchParams } from 'react-router-dom'
import Select from 'react-select';

// Redux
import { setSelectedCVs } from "../../../redux/slices/jobSlice"
import { addPendingCVs, setCvs } from "../../../redux/slices/cvSlice"

// Components
import {
  Info,
  CVItem,
  NoCv,
  UploadCvActions,
  Pagination,
  Sort,
  LabelsModal,
  DeleteConfirmModal,
  CVLimitFreemiumModal,
} from "./components"
import {
  Checkbox,
  CustomLink,
  Spinner,
  Tooltip,
} from "../../common"

// Services
import { deleteCvs, uploadCV } from "../../../services/cv"
import { updateCVLabels } from "../../../services/label"

// Hooks
import { useData } from "../../../hooks/useData"
import { useTokens } from "../../../hooks/useTokens"

// Redux
import { selectIsFreemiumUser } from "../../../redux/selectors/selectIsFreemiumUser"

// Constants and utils
import { GA_EVENTS } from "../../../constants/analytics"
import { SORT_OPTIONS } from "../../../constants"
import { filterCVs } from "./utils"

export const statuses = {
  FAILED: "Failed",
  PROCESSING: "Processing",
  DONE: "Done",
}

const UPLOAD_STATUS = {
  IDLE: "IDLE",
  DONE: "DONE",
  UPLOADING: "UPLOADING",
}

const SelectCVPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const { availableTokens, refetch: getTokens, isFetching: isFetchingTokens } = useTokens({});

  const [searchParams] = useSearchParams();
  const from = searchParams.get("from");
  // Set when the user just hit "Process" on the Sourcing page — surfaces a
  // green confirmation banner above the list. Doesn't change any data flow,
  // just a visual cue.
  const justSourcedCount = parseInt(searchParams.get("justSourced") || "0", 10) || 0;

  const { selectedCVs } = useSelector((state) => state.jobs)
  const search = useSelector((state) => state.cv.search)
  const cvLimit = useSelector((state) => state.config.freemiumPlan.CV_LIMIT)
  const isFreemiumUser = useSelector(selectIsFreemiumUser)
  
  const [showCVLimitModal, setShowCVLimitModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showLabelsModal, setShowLabelsModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [uploadingCVs, setUploadingCVs] = useState([])
  const [selectedFileId, setSelectedFileId] = useState(null);

  const {
    cvs,
    labels,
    refetchLabels,
    pendingCVs,
    pagination,
    isFetchingCVs,
  } = useData({});

  // Create a mapping function to get labels for a specific CV
  const getLabelsForCV = (cvId) => {
    if (!cvId || !labels || !Array.isArray(labels)) return [];
    
    return labels.filter(label => 
      label.cvLabels && label.cvLabels.includes(cvId)
    );
  };

  const { limit: pageSize, offset: currentPage } = pagination;

  const selectedCVsZero = selectedCVs?.length === 0;
  
  // Filters and pagination
  const [allCheckboxSelected, setAllCheckboxSelected] = useState(false);

  const [sort, setSort] = useState(SORT_OPTIONS.DESC)
  const [status, setStatus] = useState(UPLOAD_STATUS.IDLE)

  const hasCVsSelected = selectedCVs?.length > 0
  const disabled = status === UPLOAD_STATUS.UPLOADING
  const userHasCVs = cvs.length > 0 || pendingCVs?.length > 0 || uploadingCVs?.length > 0;
  
  const filteredCvs = useMemo(
    () =>
      filterCVs({ cvs, searchObject: search, sort, labels }),
    [search, sort, cvs, labels],
  )

  const processedFilteredCvs = filteredCvs?.filter(
    (cv) => cv.status === "COMPLETED",
  )

  const totalCVsInWorkspace = cvs.length + pendingCVs.length + uploadingCVs.length;
  

  useEffect(() => {
    if (from !== "sidebar") {
      dispatch(setSelectedCVs([]))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from])

  const onSelectedCvChange = ({ id, checked }) => {
    const newSetOfSelectedCvs = [...selectedCVs]
    if (checked) {
      newSetOfSelectedCvs.push(id)
    } else {
      const index = newSetOfSelectedCvs.findIndex((val) => val === id);

      newSetOfSelectedCvs.splice(index, 1);

      if (allCheckboxSelected) setAllCheckboxSelected(false);
    }

    dispatch(setSelectedCVs(newSetOfSelectedCvs))
  }

  const onDrop = async (acceptedFiles) => {
    const count = acceptedFiles.length

    if (count > availableTokens) {
      toast.error(formatMessage({ id: "toast.notEnoughCredits" }, { count }))
      return
    }

    const exceededCVLimitForFreemium = isFreemiumUser && (totalCVsInWorkspace + count) > cvLimit;

    if (exceededCVLimitForFreemium) {
      setShowCVLimitModal(true);
      return;
    }

    setStatus(UPLOAD_STATUS.UPLOADING)

    const uploadingFiles = acceptedFiles.map((file, index) => ({
      id: `${index}-${file.name}`,
      file_name: file.name,
    }))
    setUploadingCVs(uploadingFiles)

    const uploadedFiles = []

    try {
      const formData = new FormData()
      acceptedFiles.forEach((file) => formData.append("files", file))

      const { uploadedCVs } = await uploadCV(formData)

      Object.entries(uploadedCVs).forEach(([cvId, cv]) => {
        uploadedFiles.push({ cvId, file_name: cv.originalname })
      })
    } catch (err) {
      const message = err?.response?.data?.message

      if (message && message.includes(`The limit of CVs to process in your current account is ${cvLimit}`)) {
        setShowCVLimitModal(true);
        return;
      }

      if (message) {
        toast.error(message)
      }
    }

    toast.success(formatMessage(
      { id: "toast.cvsUploaded" },
      { uploadedAmount: uploadedFiles.length }
    ))

    const updatedUploadingCVs = uploadingCVs.filter(
      (file) => !uploadedFiles.some((cv) => cv.file_name === file.file_name),
    )
    setUploadingCVs(updatedUploadingCVs)

    dispatch(addPendingCVs(uploadedFiles))

    setStatus(UPLOAD_STATUS.DONE)

    // Refetch tokens to update the credits
    await getTokens();
  }

  const handleUpgrade = () => {
    navigate("/buy-more");
    setShowCVLimitModal(false)
  }
  
  const handleCancelUpload = () => {
    setShowCVLimitModal(false)
    setUploadingCVs([]);
  }

  const { getInputProps, open: openFileSelect } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      // Docx
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      // Doc
      "application/msword": [".doc"],
    },
    disabled,
  })

  if ((isFetchingCVs && !userHasCVs) || isFetchingTokens) return <Spinner />;

  const handleDeleteConfirm = async () => {
    try {
      const key = showDeleteConfirmModal

      if (!key) return;

      setDeleteLoading(true)

      await deleteCvs(selectedCVs)

      toast.success(formatMessage({ id: "toast.cvsDeleted" }))

      dispatch(setSelectedCVs([]))
      dispatch(setCvs(cvs.filter((cv) => !selectedCVs.includes(cv.id) && !selectedCVs.includes(cv.originalId))))
    } catch (err) {
      toast.error(
        err?.response?.data?.message ??
          formatMessage({ id: "toast.failedToDeleteCV" }),
      )
    }
    setShowDeleteConfirmModal(false)
    setDeleteLoading(false)
  }

  const handleCVSelectAll = ({ target }) => {
    if (target.checked) {
      dispatch(
        setSelectedCVs(processedFilteredCvs
          .filter((cv) => !cv.isDuplicated)
          .map(({ id }) => id))
      );

      return setAllCheckboxSelected(true);
    }

    dispatch(setSelectedCVs([]));
    return setAllCheckboxSelected(false);
  }

  const noSelectedCVsUploadBanner = (
    <div className="select-cv__banner">
      <h4 className="select-cv__info h4">
        <span>
          <FormattedMessage id="message.selectCandidates" />
        </span>{" "}
        <FormattedMessage id="message.forJobMatch" />
      </h4>

      <div className="select-cv__buttons">
        <UploadCvActions onUploadClick={openFileSelect} />
      </div>
    </div>
  )

  const upgradeToPremiumBanner = (
    <div className="select-cv__banner">
      <Tooltip
        tooltip={formatMessage({ id: "common.upgradeToPremiumTooltip" })}
      >
        <h4 className="select-cv__info h4">
          <span>
            <FormattedMessage id="common.noWantWaitUntilNextMonth" />
          </span>{" "}
          <FormattedMessage id="common.freemiumAccount" />
        </h4>
      </Tooltip>
  
      <div className="upgrade-to-premium__buttons">
        <UploadCvActions 
          onUploadClick={() => navigate("/choose-pricing")} 
          triggerText={formatMessage({ id: "common.upgradeToPremium" })}
        />
      </div>
    </div>
  )

  const selectedCVsUploadBanner = (
    <div className="select-cv__banner">
    <h4 className="select-cv__info h4">
      <FormattedMessage
        id="common.selected"
        values={{ count: selectedCVs?.length || 0 }}
      />
    </h4>

      <UploadCvActions
        onUploadClick={openFileSelect}
        variant="text"
        triggerText={formatMessage({ id: "common.uploadMoreCVs" })}
      />

      <button
        className="btn warning btn--small"
        onClick={() => setShowDeleteConfirmModal(true)}
      >
        <FormattedMessage id="common.delete" />
      </button>

      <div className="select-cv__buttons">
        {hasCVsSelected && (
          <CustomLink
            to={"/job-match/job-spec"}
            className="btn primary btn--small"
            trackingEvent={GA_EVENTS.SELECT_CV.ADD_JOB_DESC}
          >
            {nextIcon} <FormattedMessage id="common.nowAddJob" />
          </CustomLink>
        )}
      </div>
    </div>
  )

  const nonDuplicatedCvs = filteredCvs.filter((cv) => !cv.isDuplicated) ?? [];

  const cvListActions = userHasCVs && (
    <div className="cv-item cv-item--no-bg">
      <Checkbox
        small
        variant="secondary"
        value={allCheckboxSelected}
        onChange={handleCVSelectAll}
        label={`${formatMessage({ id: "common.all" })} (${nonDuplicatedCvs.length})`}
        // disabled={isFetchingCVs}
        disabled={!nonDuplicatedCvs.length}
      />

      <div className="flex items-center">
        <Pagination
          total={nonDuplicatedCvs.length}
          // disabled={isFetchingCVs}
        />
        <Sort 
          sort={sort} 
          setSort={setSort} 
          // disabled={isFetchingCVs}
        />
      </div>
    </div>
  );

  const uploadingCVsList = uploadingCVs?.map((file) => (
    <CVItem
      isUploading
      cv={file}
      key={`${file.id}-${file.fileName}`}
      labels={[]}
    />
  ))

  const processingCVsList = pendingCVs?.map((file) => (
    <CVItem
      isFile
      cv={file}
      // Pending CVs store `file_name` (snake_case) in `information`.
      // The previous key read `fileName` (camelCase) which was always
      // undefined — harmless but worth aligning now that sourced CVs
      // flow through the same pending list.
      key={`${file.id}-${file.information.file_name}`}
      labels={[]}
      // labels={getLabelsForCV(file.id)}
    />
  ))

  const handleLabelsIconClick = (cvId = null) => {
    setShowLabelsModal(true);
    setSelectedFileId(cvId);
  };

  const filteredCVsList = filteredCvs
    ?.slice(currentPage, currentPage + pageSize)
    .map((cv) => (
      <CVItem
        cv={cv}
        key={`${cv.id}-${cv.status}`}
        viewLink={`/job-match/select-cv/${cv.isDuplicated ? cv.originalId : cv.id}`}
        selected={!cv.isDuplicated && selectedCVs?.includes(cv.id)}
        onChange={({ id, checked }) => onSelectedCvChange({ id, checked })}
        labels={getLabelsForCV(cv.id)}
        handleLabelsIconClick={handleLabelsIconClick}
      />
    ));

  const handleLabelsModalConfirm = async (selectedLabelIds) => {
    await updateCVLabels(selectedFileId, selectedLabelIds);
    // Refetch labels data to update the UI with the new assignments
    await refetchLabels();

    setSelectedFileId(null);
  };

  return (
    <div className="select-cv">
      <input {...getInputProps()} multiple={true} />
      {userHasCVs && (
        <>
          {(() => {
            if (availableTokens <= 0 && isFreemiumUser) {
              return upgradeToPremiumBanner;
            }
            if (selectedCVsZero) {
              return noSelectedCVsUploadBanner;
            }
            return selectedCVsUploadBanner;
          })()}
        </>
      )}

      {(!cvs || cvs?.length === 0) &&
        pendingCVs?.length === 0 &&
        <NoCv onUploadClick={openFileSelect} />}

      {justSourcedCount > 0 && (
        <div className="just-sourced-banner">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M5 13l4 4L19 7" />
          </svg>
          <FormattedMessage
            id="sourcing.justSourced.banner"
            values={{ count: justSourcedCount }}
          />
        </div>
      )}

      <Info />

      <div className="select-cv__cv-wrapper">
        {cvListActions}
        {/* {isFetchingCVs && <Spinner className="small" variant="primary" />} */}

        {uploadingCVsList}
        {processingCVsList}
        {filteredCVsList}
      </div>

      <DeleteConfirmModal
        cvsLength={selectedCVs?.length || 0}
        open={showDeleteConfirmModal}
        setOpen={setShowDeleteConfirmModal}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirmModal(false)}
        loading={deleteLoading}
      />

      <CVLimitFreemiumModal
        open={showCVLimitModal}
        setOpen={setShowCVLimitModal}
        onUpgrade={handleUpgrade}
        onCancel={handleCancelUpload}
        cvLimit={cvLimit}
      />

      <LabelsModal
        open={showLabelsModal}
        setOpen={setShowLabelsModal}
        onConfirm={handleLabelsModalConfirm}
        onCancel={() => setShowLabelsModal(false)}
        labels={labels}
        preSelectedLabels={getLabelsForCV(selectedFileId)}
      />
    </div>
  )
}

const nextIcon = (
  <svg
    width="25"
    height="26"
    viewBox="0 0 25 26"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect y="0.5625" width="25" height="25" rx="12.5" fill="currentColor" />
    <path
      d="M9 13.0625H16M16 13.0625L13 9.0625M16 13.0625L13 17.0625"
      stroke="#5EC58F"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export default SelectCVPage
